"use client"

import { useState } from "react"
import {
  BadgeCheck,
  MousePointer2,
  Clock,
  Video,
  User,
  Mail,
  Building2,
  ArrowRight,
  Send,
  Phone
} from "lucide-react"

export default function Contact() {
  const [interest, setInterest] = useState("Speaking")
  const [preferredContact, setPreferredContact] = useState("Email")

  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  })

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null as string | null,
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus({ loading: true, success: false, error: null })

    const formWithContext = {
      ...formState,
      interest,
      preferredContact,
      subject: `[RG-Inquiry] ${interest} - ${formState.name} from ${formState.company}`,
    }

    try {
      const response = await fetch(
        "/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formWithContext),
        }
      )

      if (!response.ok) throw new Error("Failed to send message")

      setStatus({ loading: false, success: true, error: null })
      setFormState({ name: "", email: "", phone: "", company: "", message: "" })
    } catch (error) {
      setStatus({
        loading: false,
        success: false,
        error: "Failed to send message. Please try again later.",
      })
    }
  }

  return (
    <section id="contact" className="relative py-12 bg-[#020617] overflow-hidden">
      <div className="max-w-[1800px] mx-auto px-8 lg:px-12 relative z-10">
        <div className="bg-white rounded-[40px] shadow-2xl overflow-hidden border border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* Left Column: Context & Process */}
            <div className="p-8 lg:py-12 lg:pl-12 lg:pr-6 bg-slate-50/50">
              <div className="w-full space-y-8">
                <div className="space-y-4">
                  <h2 className="text-5xl lg:text-6xl font-black text-slate-900 leading-tight tracking-tight font-[family-name:var(--font-plus-jakarta-sans)]">
                    Partner with a <br />
                    Leader in <span className="text-blue-600">AI Agents</span>
                  </h2>
                  <p className="text-base text-slate-600 leading-relaxed font-[family-name:var(--font-plus-jakarta-sans)]">
                    Available for keynotes, workshops, and strategic advisory. Transform your business with actionable AI insights.
                  </p>
                </div>

                {/* Areas of Expertise */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-bold text-slate-900 font-[family-name:var(--font-plus-jakarta-sans)]">
                      Areas of Expertise
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Autonomous Agents",
                      "AI Ethics & Governance",
                      "Enterprise Integration",
                      "LLM Strategy",
                      "Future of Work"
                    ].map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 font-[family-name:var(--font-plus-jakarta-sans)] shadow-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Booking Process Stepper */}
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-900 font-[family-name:var(--font-plus-jakarta-sans)]">
                    Booking Process
                  </h3>
                  <div className="space-y-6 relative">
                    {/* Connecting Line */}
                    <div className="absolute left-[19px] top-4 bottom-4 w-0.5 bg-slate-200" />

                    {[
                      {
                        icon: MousePointer2,
                        title: "Submit Inquiry",
                        desc: "Fill out the form with your event details or project scope."
                      },
                      {
                        icon: Clock,
                        title: "Review & Response",
                        desc: "Rakesh reviews requests personally. Expect a reply within 24-48 hours."
                      },
                      {
                        icon: Video,
                        title: "Initial Consultation",
                        desc: "A 30-minute discovery call to align on goals and confirm details."
                      }
                    ].map((step, idx) => (
                      <div key={idx} className="flex gap-4 relative z-10 group">
                        <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0 group-hover:border-blue-500 transition-colors">
                          <step.icon className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                        <div className="space-y-0.5 py-0.5">
                          <h4 className="font-bold text-slate-900 text-base font-[family-name:var(--font-plus-jakarta-sans)]">
                            {step.title}
                          </h4>
                          <p className="text-xs text-slate-500 leading-relaxed font-[family-name:var(--font-plus-jakarta-sans)] max-w-xs">
                            {step.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Inquiry Form */}
            <div className="p-8 lg:py-12 lg:pr-12 lg:pl-6 bg-white">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-1">
                  <h3 className="text-2xl font-bold text-slate-900 font-[family-name:var(--font-plus-jakarta-sans)]">
                    Start a Conversation
                  </h3>
                </div>

                {/* Interest Selection */}
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 font-[family-name:var(--font-plus-jakarta-sans)]">
                    I am interested in
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {["Speaking", "Consultation", "Other"].map((btn) => (
                      <button
                        key={btn}
                        type="button"
                        onClick={() => setInterest(btn)}
                        className={`py-2.5 rounded-lg font-bold text-xs transition-all shadow-sm border ${interest === btn
                          ? "bg-blue-600 text-white border-blue-600 ring-4 ring-blue-500/10"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                          }`}
                      >
                        {btn}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Name */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 font-[family-name:var(--font-plus-jakarta-sans)]">Full Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          name="name"
                          value={formState.name}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-3 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all font-medium text-slate-900 text-sm"
                          placeholder="Your Full Name"
                        />
                      </div>
                    </div>
                    {/* Email */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 font-[family-name:var(--font-plus-jakarta-sans)]">Work Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="email"
                          name="email"
                          value={formState.email}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-3 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all font-medium text-slate-900 text-sm"
                          placeholder="Your Work Email"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Company */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 font-[family-name:var(--font-plus-jakarta-sans)]">Company / Organization</label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          name="company"
                          value={formState.company}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-3 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all font-medium text-slate-900 text-sm"
                          placeholder="Your Company Name"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 font-[family-name:var(--font-plus-jakarta-sans)]">Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formState.phone}
                          onChange={handleChange}
                          className="w-full pl-10 pr-3 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all font-medium text-slate-900 text-sm"
                          placeholder="Your Phone Number"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 font-[family-name:var(--font-plus-jakarta-sans)]">Project Details / Message</label>
                    <textarea
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      required
                      rows={3}
                      className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all font-medium text-slate-900 min-h-[100px] text-sm"
                      placeholder="Tell us about the event date, audience size, or consultation goals..."
                    />
                  </div>
                </div>

                {/* Preferred Contact & Submit */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-4">
                  <div className="flex items-center gap-6">
                    <span className="text-sm font-bold text-slate-400 font-[family-name:var(--font-plus-jakarta-sans)]">Preferred contact:</span>
                    {["Email", "Phone"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setPreferredContact(opt)}
                        className="flex items-center gap-2 group cursor-pointer"
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${preferredContact === opt ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-white group-hover:border-slate-400"
                          }`}>
                          <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        </div>
                        <span className={`text-sm font-bold font-[family-name:var(--font-plus-jakarta-sans)] ${preferredContact === opt ? "text-slate-900" : "text-slate-500"
                          }`}>{opt}</span>
                      </button>
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={status.loading}
                    className="flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-base shadow-lg shadow-blue-500/20 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {status.loading ? "Sending..." : "Submit Inquiry"}
                    {!status.loading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </div>

                {status.success && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-2xl text-green-700 font-bold text-sm text-center">
                    Message sent successfully! We'll be in touch soon.
                  </div>
                )}
                {status.error && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-700 font-bold text-sm text-center">
                    {status.error}
                  </div>
                )}
              </form>
            </div>

          </div>
        </div>
      </div>
    </section>
  )
}
